import React, { useEffect, useRef, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { useIsStaff } from "@/hooks/useIsStaff";
import { isPathAllowedForStaff, STAFF_LANDING_PATH } from "@/lib/staffAccess";
import { staffHasFullCustomerAccess } from "@/lib/staffLoginAdmin";
import { AIChatbotSidebar } from "@/components/chatbot/AIChatbotSidebar";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTableResponsive } from "@/hooks/use-table-responsive";
import { useChatbot } from "@/hooks/useChatbot";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";

const SIDEBAR_MODE_KEY = "sidebar-mode";
type SidebarMode = "expanded" | "collapsed" | "hover";

/**
 * Ladebildschirm beim Start der App.
 *
 * Hier stand vorher nur ein endlos drehender Kreis. Wenn die Datenbank
 * langsam war, sahen die Kunden diesen Kreis minutenlang und ohne jeden
 * Hinweis -- genau das Bild aus den Fehlermeldungen vom September 2026.
 *
 * Bewusst eine eigene Komponente: So haengt der Timer am Mount und setzt sich
 * von selbst zurueck, wenn das Laden fertig ist.
 */
const InitializingScreen: React.FC = () => {
  const { t } = useTranslation();
  const [tooLong, setTooLong] = useState(false);

  useEffect(() => {
    // Grosszuegig gewaehlt, damit ein normaler Ladevorgang nicht faelschlich
    // wie eine Stoerung aussieht.
    const timer = window.setTimeout(() => setTooLong(true), 10_000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      {tooLong && (
        <>
          <h1 className="text-base font-medium">{t("stillLoadingTitle")}</h1>
          <p className="max-w-sm text-sm text-muted-foreground">{t("stillLoadingBody")}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            {t("reloadPage")}
          </Button>
        </>
      )}
    </div>
  );
};

export const Layout: React.FC = () => {
  // Sidebar display mode (Supabase-style): expanded | collapsed | expand-on-hover.
  // Default is "hover" (collapsed, expands as an overlay on hover).
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(() => {
    const stored = localStorage.getItem(SIDEBAR_MODE_KEY) as SidebarMode | null;
    return stored === "expanded" || stored === "collapsed" || stored === "hover" ? stored : "hover";
  });
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  const isMobile = useIsMobile();
  const { isCompact } = useTableResponsive();
  const {
    tenants,
    currentTenant: _currentTenant,
    isInitializing,
    tenantsLoadError,
    retryTenants,
  } = useTenant();

  const isStaff = useIsStaff();
  const location = useLocation();
  const { signOut } = useAuth();
  const { isOpen: isChatbotOpen, toggleChatbot } = useChatbot();
  const { toast } = useToast();
  const { t } = useTranslation();

  // Persist sidebar mode to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_MODE_KEY, sidebarMode);
  }, [sidebarMode]);

  // Detect coarse pointer (touch devices like iPad)
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(hover: none) and (pointer: coarse)');
    const update = () => {
      const hasTouchEvent = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsTouchDevice(mql.matches || hasTouchEvent);
    };
    try {
      mql.addEventListener('change', update);
    } catch {
      // Safari fallback
      // @ts-ignore
      mql.addListener(update);
    }
    update();
    return () => {
      try {
        mql.removeEventListener('change', update);
      } catch {
        // @ts-ignore
        mql.removeListener(update);
      }
    };
  }, []);

  // On touch devices that are not in the mobile breakpoint,
  // default the desktop sidebar to collapsed once.
  const appliedTouchDefaultRef = useRef(false);
  useEffect(() => {
    if (!appliedTouchDefaultRef.current && isTouchDevice && !isMobile) {
      // Hover-to-expand is pointless on touch devices — fall back to collapsed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSidebarMode((prev) => (prev === "hover" ? "collapsed" : prev));
      appliedTouchDefaultRef.current = true;
    }
  }, [isTouchDevice, isMobile]);

  // Auto-collapse sidebar when space is limited (chatbot open + narrow screen)
  const shouldAutoCollapse = !isMobile && isChatbotOpen && isCompact;
  // In "hover" mode the bar is collapsed until hovered; "collapsed" is always collapsed.
  const baseCollapsed = sidebarMode === "collapsed" || (sidebarMode === "hover" && !isSidebarHovered);
  const effectiveSidebarCollapsed = baseCollapsed || shouldAutoCollapse;
  // Content only reserves the wide width in "expanded" mode; in hover mode the
  // expanded bar overlays the content instead of pushing it.
  const reserveWide = sidebarMode === "expanded" && !shouldAutoCollapse;

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: t("error"),
        description: t('failedToSignOut'),
        variant: "destructive",
      });
    }
  };

  // Debug: Track layout re-renders and mobile state changes
  const renderCount = useRef(0);
  const previousMobile = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    renderCount.current += 1;
    if (previousMobile.current !== isMobile) {
      previousMobile.current = isMobile;
    }
  }, [isMobile]);

  // Konnte die Salon-Liste gar nicht geladen werden, ist das eine Stoerung --
  // und *nicht* "kein Salon zugeordnet". Genau diese Verwechslung liess die
  // Kunden glauben, sie kaemen nicht mehr in ihren Account.
  if (!isInitializing && tenantsLoadError && tenants.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <h1 className="text-lg font-semibold">{t("connectionProblemTitle")}</h1>
        <p className="max-w-sm text-sm text-muted-foreground">{t("connectionProblemBody")}</p>
        <Button onClick={() => retryTenants()}>{t("reloadPage")}</Button>
      </div>
    );
  }

  // If we're still initializing, show loading state
  if (isInitializing) {
    return <InitializingScreen />;
  }

  // If user has no tenants, show a message that access will be granted externally
  if (tenants.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        {/* Logout button in top right */}
        <div className="flex justify-end p-4">
          <Button variant="outline" size="sm" onClick={handleSignOut}>
            <LogOut className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("signOut")}</span>
          </Button>
        </div>

        {/* Centered content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-semibold text-gray-900 mb-4">{t("welcomeToSylva")}</h1>
            <p className="text-gray-600 mb-2">{t("accountBeingSetUp")}</p>
            <p className="text-sm text-gray-500">{t("workspaceAccessPending")}</p>
          </div>
        </div>
      </div>
    );
  }

  // Staff (own-calendar-only logins) may only reach the calendar — plus the
  // customer list when the salon granted full customer access. Bounce every
  // other route. RLS is the real barrier; this is the UI gate.
  if (
    isStaff &&
    !isPathAllowedForStaff(location.pathname, {
      customerList: staffHasFullCustomerAccess(_currentTenant?.settings),
    })
  ) {
    return <Navigate to={STAFF_LANDING_PATH} replace />;
  }

  return (
    <div className="h-screen flex w-full bg-background max-w-full overflow-hidden">
      {/* Desktop sidebar - hidden on mobile */}
      {!isMobile && (
        <Sidebar
          collapsed={effectiveSidebarCollapsed}
          sidebarMode={sidebarMode}
          onSidebarModeChange={setSidebarMode}
          onHoverChange={setIsSidebarHovered}
        />
      )}

      {/* Main content area - always present */}
      {/* Pages now provide their own header via PageWrapper */}
      <div className={`flex-1 flex flex-col min-w-0 min-h-0 ${!isMobile ? (reserveWide ? "ml-54" : "ml-20") : ""} ${isChatbotOpen && !isMobile ? "mr-96" : ""}`}>
        <main className="flex-1 min-w-0 min-h-0 overflow-hidden">
          <Outlet />
        </main>
      </div>

      {/* Chatbot Sidebar */}
      {isChatbotOpen && (
        <>
          {isMobile ? (
            /* Mobile chatbot as sheet overlay */
            <Sheet open={isChatbotOpen} onOpenChange={toggleChatbot}>
              <SheetContent side="right" className="p-0 w-full max-w-md">
                <AIChatbotSidebar />
              </SheetContent>
            </Sheet>
          ) : (
            /* Desktop chatbot as fixed sidebar */
            <div className="fixed right-0 top-0 h-full w-96 bg-white border-l shadow-lg z-30">
              <AIChatbotSidebar onClose={toggleChatbot} />
            </div>
          )}
        </>
      )}
      <PWAInstallPrompt />
    </div>
  );
};
